import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useState } from 'react';
import { Button } from '@/components/admin/ui/button';
import { Input } from '@/components/admin/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/admin/ui/table';
import { track } from '@/lib/analytics';
import { ADMIN_PACIFIC_TZ } from '@/lib/adminPacificTime';
import { apiJson } from '@/lib/api';
import type {
  AdminDashboardMetricsResponse,
  AdminUserRow,
  AdminUsersResponse,
} from '@/lib/types';

const PAGE_SIZE = 50;

type AdminUsersSortKey =
  | 'id'
  | 'name'
  | 'email'
  | 'phone'
  | 'created_at'
  | 'subscribed'
  | 'deleted'
  | 'messages'
  | 'expenses';

function defaultSortDirForColumn(key: AdminUsersSortKey): 'asc' | 'desc' {
  if (key === 'name' || key === 'email' || key === 'phone') return 'asc';
  return 'desc';
}

// Phone numbers are stored as E.164 (+1XXXXXXXXXX), but admins often paste
// formatted numbers like "(484) 695-8563" (sometimes with unicode dashes).
// When the query looks like a phone (enough digits, no letters/email), strip
// non-digits so the server's ILIKE substring match still hits the stored value.
function normalizeAdminSearchQuery(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return '';
  if (trimmed.includes('@')) return trimmed;
  const digits = trimmed.replace(/\D/g, '');
  if (digits.length >= 7 && !/[a-zA-Z]/.test(trimmed)) {
    return digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  }
  return trimmed;
}

function SortTh({
  label,
  sortKey,
  activeKey,
  dir,
  onSort,
  align = 'left',
  title,
}: {
  label: string;
  sortKey: AdminUsersSortKey;
  activeKey: AdminUsersSortKey;
  dir: 'asc' | 'desc';
  onSort: (key: AdminUsersSortKey) => void;
  align?: 'left' | 'right';
  title?: string;
}) {
  const active = activeKey === sortKey;
  const alignClass = align === 'right' ? 'text-right' : 'text-left';
  return (
    <TableHead
      scope='col'
      title={title}
      className={`${alignClass} ${align === 'right' ? 'tabular-nums' : ''}`}
    >
      <button
        type='button'
        onClick={() => onSort(sortKey)}
        aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}
        className={`group inline-flex w-full items-center gap-1 text-[11px] font-medium uppercase tracking-wide text-nova-muted-foreground hover:text-nova-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-nova-ring focus-visible:ring-offset-2 rounded-sm ${align === 'right' ? 'justify-end' : 'justify-start'}`}
      >
        <span>{label}</span>
        {active ? (
          <span className='text-nova-primary tabular-nums' aria-hidden>
            {dir === 'asc' ? '↑' : '↓'}
          </span>
        ) : (
          <span className='text-nova-muted-foreground opacity-0 group-hover:opacity-100' aria-hidden>
            ↕
          </span>
        )}
      </button>
    </TableHead>
  );
}

const USER_VIEW_LINK_CLASS =
  'inline-flex px-2 py-1 rounded-md border border-nova-input text-xs font-semibold text-nova-foreground hover:bg-nova-accent';

const MOBILE_SORT_OPTIONS: Array<[AdminUsersSortKey, string]> = [
  ['created_at', 'Created'],
  ['id', 'ID'],
  ['name', 'Name'],
  ['email', 'Email'],
  ['phone', 'Phone'],
  ['subscribed', 'Subscribed'],
  ['deleted', 'Deleted'],
  ['messages', 'Messages'],
  ['expenses', 'Expenses'],
];

function userDisplayName(u: AdminUserRow): string {
  return [u.first_name, u.last_name].filter(Boolean).join(' ') || '—';
}

function AdminUserCard({ u }: { u: AdminUserRow }) {
  const name = userDisplayName(u);
  const created = new Date(u.created_at).toLocaleDateString(undefined, {
    timeZone: ADMIN_PACIFIC_TZ,
  });
  return (
    <Link
      to='/admin/users/$userId'
      params={{ userId: String(u.id) }}
      search={{ hid: undefined }}
      onClick={() => track('Admin User Row Clicked', { source: 'card' })}
      className='block rounded-lg border border-nova-border bg-nova-card p-4 shadow-sm hover:bg-nova-accent active:bg-nova-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-nova-ring focus-visible:ring-offset-2'
    >
      <div className='flex items-start justify-between gap-3'>
        <div className='min-w-0'>
          <p className='truncate font-medium text-nova-foreground'>{name}</p>
          <p className='truncate text-xs text-nova-muted-foreground'>{u.email ?? '—'}</p>
          {u.phone_number ? (
            <p className='truncate text-xs text-nova-muted-foreground'>{u.phone_number}</p>
          ) : null}
          <p className='mt-1 text-[11px] tabular-nums text-nova-muted-foreground'>
            #{u.id} · {created}
          </p>
        </div>
        <span className={`${USER_VIEW_LINK_CLASS} pointer-events-none shrink-0`}>
          View
        </span>
      </div>
      <div className='mt-3 flex flex-wrap items-center gap-1.5'>
        {u.is_professional ? (
          <span className='inline-flex rounded-md bg-violet-100 px-2 py-0.5 text-xs font-semibold text-violet-800'>
            Pro
          </span>
        ) : null}
        {u.calm_filter_enabled ? (
          <span className='inline-flex rounded-md bg-sky-100 px-2 py-0.5 text-xs font-semibold text-sky-800'>
            Calm
          </span>
        ) : null}
        {u.subscribed ? (
          <span className='inline-flex rounded-md bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-700'>
            Subscribed{u.subscription_override ? ' (override)' : ''}
          </span>
        ) : (
          <span className='inline-flex rounded-md bg-nova-muted px-2 py-0.5 text-xs font-semibold text-nova-muted-foreground'>
            Not subscribed
          </span>
        )}
        {u.deleted_at ? (
          <span className='inline-flex rounded-md bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700'>
            Deleted
          </span>
        ) : null}
        <span className='text-xs tabular-nums text-nova-muted-foreground'>
          {u.household_message_count.toLocaleString()} msgs ·{' '}
          {u.household_expense_count.toLocaleString()} expenses
        </span>
      </div>
    </Link>
  );
}

function AdminUsersPage() {
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [subscribed, setSubscribed] = useState<'all' | 'yes' | 'no'>('all');
  const [professionalOnly, setProfessionalOnly] = useState(false);
  const [calmFilterOnly, setCalmFilterOnly] = useState(false);
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [page, setPage] = useState(0);
  const [sortBy, setSortBy] = useState<AdminUsersSortKey>('created_at');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const onSortColumn = (key: AdminUsersSortKey) => {
    setPage(0);
    if (sortBy === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(key);
      setSortDir(defaultSortDirForColumn(key));
    }
  };

  const params = new URLSearchParams();
  const normalizedQ = normalizeAdminSearchQuery(q);
  if (normalizedQ) params.set('q', normalizedQ);
  if (subscribed === 'yes') params.set('subscribed', 'true');
  if (subscribed === 'no') params.set('subscribed', 'false');
  if (professionalOnly) params.set('professional_only', 'true');
  if (calmFilterOnly) params.set('calm_filter_enabled', 'true');
  if (includeDeleted) params.set('include_deleted', 'true');
  params.set('limit', String(PAGE_SIZE));
  params.set('offset', String(page * PAGE_SIZE));
  params.set('sort_by', sortBy);
  params.set('sort_dir', sortDir);

  const usersQuery = useQuery({
    queryKey: ['admin', 'users', params.toString()],
    queryFn: () =>
      apiJson<AdminUsersResponse>(getToken, `/v1/admin/users?${params.toString()}`),
    placeholderData: keepPreviousData,
  });

  const metricsQuery = useQuery({
    queryKey: ['admin', 'metrics'],
    queryFn: () => apiJson<AdminDashboardMetricsResponse>(getToken, '/v1/admin/metrics'),
  });

  const data = usersQuery.data;
  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

  const usd = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2,
  });

  return (
    <div className='space-y-4'>
      <div className='grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-3'>
        <div className='bg-nova-card rounded-lg shadow-sm border border-nova-border p-4'>
          <p className='text-xs font-semibold uppercase tracking-wide text-nova-muted-foreground'>
            Total users
          </p>
          {metricsQuery.isLoading ? (
            <p className='mt-1 text-2xl font-semibold text-nova-muted-foreground'>—</p>
          ) : metricsQuery.isError ? (
            <p className='mt-1 text-sm text-red-600'>Could not load</p>
          ) : (
            <p className='mt-1 text-2xl font-semibold tabular-nums text-nova-foreground'>
              {(metricsQuery.data?.total_user_count ?? 0).toLocaleString()}
            </p>
          )}
          <p className='mt-1 text-xs text-nova-muted-foreground'>Accounts not deleted</p>
        </div>
        <div className='bg-nova-card rounded-lg shadow-sm border border-nova-border p-4'>
          <p className='text-xs font-semibold uppercase tracking-wide text-nova-muted-foreground'>
            Professional users
          </p>
          {metricsQuery.isLoading ? (
            <p className='mt-1 text-2xl font-semibold text-nova-muted-foreground'>—</p>
          ) : metricsQuery.isError ? (
            <p className='mt-1 text-sm text-red-600'>Could not load</p>
          ) : (
            <p className='mt-1 text-2xl font-semibold tabular-nums text-nova-foreground'>
              {(metricsQuery.data?.professional_user_count ?? 0).toLocaleString()}
            </p>
          )}
          <p className='mt-1 text-xs text-nova-muted-foreground'>
            Email-only accounts with no phone (matches Pro web sign-up)
          </p>
        </div>
        <div className='bg-nova-card rounded-lg shadow-sm border border-nova-border p-4'>
          <p className='text-xs font-semibold uppercase tracking-wide text-nova-muted-foreground'>
            Subscribed users
          </p>
          {metricsQuery.isLoading ? (
            <p className='mt-1 text-2xl font-semibold text-nova-muted-foreground'>—</p>
          ) : metricsQuery.isError ? (
            <p className='mt-1 text-sm text-red-600'>Could not load</p>
          ) : (
            <p className='mt-1 text-2xl font-semibold tabular-nums text-nova-foreground'>
              {(metricsQuery.data?.subscribed_user_count ?? 0).toLocaleString()}
            </p>
          )}
          <p className='mt-1 text-xs text-nova-muted-foreground'>
            Active subscription or admin override
          </p>
        </div>
        <div className='bg-nova-card rounded-lg shadow-sm border border-nova-border p-4'>
          <p className='text-xs font-semibold uppercase tracking-wide text-nova-muted-foreground'>
            Professional invites pending
          </p>
          {metricsQuery.isLoading ? (
            <p className='mt-1 text-2xl font-semibold text-nova-muted-foreground'>—</p>
          ) : metricsQuery.isError ? (
            <p className='mt-1 text-sm text-red-600'>Could not load</p>
          ) : (
            <p className='mt-1 text-2xl font-semibold tabular-nums text-nova-foreground'>
              {(metricsQuery.data?.professional_invite_pending_count ?? 0).toLocaleString()}
            </p>
          )}
          <p className='mt-1 text-xs text-nova-muted-foreground'>
            Email invite sent; professional has not accepted yet
          </p>
        </div>
        <div className='bg-nova-card rounded-lg shadow-sm border border-nova-border p-4'>
          <p className='text-xs font-semibold uppercase tracking-wide text-nova-muted-foreground'>
            Professional invites accepted
          </p>
          {metricsQuery.isLoading ? (
            <p className='mt-1 text-2xl font-semibold text-nova-muted-foreground'>—</p>
          ) : metricsQuery.isError ? (
            <p className='mt-1 text-sm text-red-600'>Could not load</p>
          ) : (
            <p className='mt-1 text-2xl font-semibold tabular-nums text-nova-foreground'>
              {(metricsQuery.data?.professional_invite_accepted_count ?? 0).toLocaleString()}
            </p>
          )}
          <p className='mt-1 text-xs text-nova-muted-foreground'>
            Professional accepted the invite (connected households)
          </p>
        </div>
      </div>

      <div className='grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3'>
        <div className='bg-nova-card rounded-lg shadow-sm border border-nova-border p-4'>
          <p className='text-xs font-semibold uppercase tracking-wide text-nova-muted-foreground'>
            Expenses (app-wide)
          </p>
          {metricsQuery.isLoading ? (
            <p className='mt-1 text-2xl font-semibold text-nova-muted-foreground'>—</p>
          ) : metricsQuery.isError ? (
            <p className='mt-1 text-sm text-red-600'>Could not load</p>
          ) : (
            <p className='mt-1 text-2xl font-semibold tabular-nums text-nova-foreground'>
              {(metricsQuery.data?.expense_count ?? 0).toLocaleString()}
            </p>
          )}
          <p className='mt-1 text-xs text-nova-muted-foreground'>Non-deleted expense records</p>
        </div>
        <div className='bg-nova-card rounded-lg shadow-sm border border-nova-border p-4'>
          <p className='text-xs font-semibold uppercase tracking-wide text-nova-muted-foreground'>
            Amount tracked
          </p>
          {metricsQuery.isLoading ? (
            <p className='mt-1 text-2xl font-semibold text-nova-muted-foreground'>—</p>
          ) : metricsQuery.isError ? (
            <p className='mt-1 text-sm text-red-600'>Could not load</p>
          ) : (
            <p className='mt-1 text-2xl font-semibold tabular-nums text-nova-foreground'>
              {usd.format((metricsQuery.data?.expense_amount_cents ?? 0) / 100)}
            </p>
          )}
          <p className='mt-1 text-xs text-nova-muted-foreground'>Sum of expense amounts (USD)</p>
        </div>
        <div className='bg-nova-card rounded-lg shadow-sm border border-nova-border p-4'>
          <p className='text-xs font-semibold uppercase tracking-wide text-nova-muted-foreground'>
            Journal entries
          </p>
          {metricsQuery.isLoading ? (
            <p className='mt-1 text-2xl font-semibold text-nova-muted-foreground'>—</p>
          ) : metricsQuery.isError ? (
            <p className='mt-1 text-sm text-red-600'>Could not load</p>
          ) : (
            <p className='mt-1 text-2xl font-semibold tabular-nums text-nova-foreground'>
              {(metricsQuery.data?.journal_entry_count ?? 0).toLocaleString()}
            </p>
          )}
          <p className='mt-1 text-xs text-nova-muted-foreground'>Non-deleted entries (app-wide)</p>
        </div>
        <div className='bg-nova-card rounded-lg shadow-sm border border-nova-border p-4'>
          <p className='text-xs font-semibold uppercase tracking-wide text-nova-muted-foreground'>
            Messages
          </p>
          {metricsQuery.isLoading ? (
            <p className='mt-1 text-2xl font-semibold text-nova-muted-foreground'>—</p>
          ) : metricsQuery.isError ? (
            <p className='mt-1 text-sm text-red-600'>Could not load</p>
          ) : (
            <p className='mt-1 text-2xl font-semibold tabular-nums text-nova-foreground'>
              {(metricsQuery.data?.message_count ?? 0).toLocaleString()}
            </p>
          )}
          <p className='mt-1 text-xs text-nova-muted-foreground'>
            Household chat only (excludes dispute-thread messages)
          </p>
        </div>
      </div>

      <div className='bg-nova-card rounded-lg shadow-sm border border-nova-border p-4 flex flex-wrap gap-3 items-end'>
        <div className='flex-1 min-w-[240px]'>
          <label className='block text-xs font-semibold text-nova-muted-foreground mb-1'>
            Search (name, email, phone, user ID)
          </label>
          <Input
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setPage(0);
            }}
            placeholder='e.g. jane@example.com or 12345'
          />
        </div>
        <div>
          <label className='block text-xs font-semibold text-nova-muted-foreground mb-1'>
            Subscribed
          </label>
          <select
            value={subscribed}
            onChange={(e) => {
              setSubscribed(e.target.value as 'all' | 'yes' | 'no');
              setPage(0);
            }}
            className='px-3 py-2 border border-nova-input rounded-md text-sm'
          >
            <option value='all'>All</option>
            <option value='yes'>Subscribed</option>
            <option value='no'>Not subscribed</option>
          </select>
        </div>
        <label
          className='flex items-center gap-2 text-sm text-nova-foreground'
          title='Email-only accounts with no phone on file'
        >
          <input
            type='checkbox'
            checked={professionalOnly}
            onChange={(e) => {
              setProfessionalOnly(e.target.checked);
              setPage(0);
            }}
          />
          Professionals only
        </label>
        <label
          className='flex items-center gap-2 text-sm text-nova-foreground'
          title='Users who turned on Calm Filter'
        >
          <input
            type='checkbox'
            checked={calmFilterOnly}
            onChange={(e) => {
              setCalmFilterOnly(e.target.checked);
              setPage(0);
            }}
          />
          Calm filter on
        </label>
        <label className='flex items-center gap-2 text-sm text-nova-foreground'>
          <input
            type='checkbox'
            checked={includeDeleted}
            onChange={(e) => {
              setIncludeDeleted(e.target.checked);
              setPage(0);
            }}
          />
          Include deleted
        </label>
        <div className='flex items-center gap-2 lg:hidden'>
          <label className='text-xs font-semibold text-nova-muted-foreground'>
            Sort
            <select
              value={sortBy}
              onChange={(e) => {
                const next = e.target.value as AdminUsersSortKey;
                setPage(0);
                setSortBy(next);
                setSortDir(defaultSortDirForColumn(next));
              }}
              className='ml-2 px-3 py-2 border border-nova-input rounded-md text-sm font-normal text-nova-foreground'
            >
              {MOBILE_SORT_OPTIONS.map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <button
            type='button'
            onClick={() => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))}
            className='px-3 py-2 border border-nova-input rounded-md text-sm text-nova-foreground'
            aria-label={sortDir === 'asc' ? 'Sort ascending' : 'Sort descending'}
          >
            {sortDir === 'asc' ? '↑' : '↓'}
          </button>
        </div>
      </div>

      {usersQuery.isLoading && (
        <div className='bg-nova-card rounded-lg shadow-sm border border-nova-border px-4 py-8 text-center text-nova-muted-foreground lg:hidden'>
          Loading…
        </div>
      )}
      {usersQuery.isError && (
        <div className='bg-nova-card rounded-lg shadow-sm border border-nova-border px-4 py-8 text-center text-red-500 lg:hidden'>
          Failed to load users.
        </div>
      )}
      {data && data.users.length === 0 && !usersQuery.isLoading && (
        <div className='bg-nova-card rounded-lg shadow-sm border border-nova-border px-4 py-8 text-center text-nova-muted-foreground lg:hidden'>
          No users match.
        </div>
      )}
      {data && data.users.length > 0 && (
        <div className='space-y-3 lg:hidden'>
          {data.users.map((u) => (
            <AdminUserCard key={u.id} u={u} />
          ))}
        </div>
      )}

      <div className='hidden overflow-hidden rounded-[var(--nova-radius)] border border-nova-border bg-nova-card shadow-sm lg:block'>
        <Table>
          <TableHeader>
            <TableRow className='hover:bg-transparent'>
              <SortTh
                label='ID'
                sortKey='id'
                activeKey={sortBy}
                dir={sortDir}
                onSort={onSortColumn}
              />
              <SortTh
                label='Name'
                sortKey='name'
                activeKey={sortBy}
                dir={sortDir}
                onSort={onSortColumn}
              />
              <SortTh
                label='Email'
                sortKey='email'
                activeKey={sortBy}
                dir={sortDir}
                onSort={onSortColumn}
              />
              <SortTh
                label='Phone'
                sortKey='phone'
                activeKey={sortBy}
                dir={sortDir}
                onSort={onSortColumn}
              />
              <TableHead scope='col' title='Email-only sign-up with no phone on file'>
                Pro
              </TableHead>
              <TableHead scope='col' title='Calm Filter enabled in app settings'>
                Calm
              </TableHead>
              <SortTh
                label='Created'
                sortKey='created_at'
                activeKey={sortBy}
                dir={sortDir}
                onSort={onSortColumn}
              />
              <SortTh
                label='Subscribed'
                sortKey='subscribed'
                activeKey={sortBy}
                dir={sortDir}
                onSort={onSortColumn}
              />
              <SortTh
                label='Deleted'
                sortKey='deleted'
                activeKey={sortBy}
                dir={sortDir}
                onSort={onSortColumn}
              />
              <SortTh
                label='Messages'
                sortKey='messages'
                activeKey={sortBy}
                dir={sortDir}
                onSort={onSortColumn}
                align='right'
                title='Non-dispute chat messages in all households this user belongs to'
              />
              <SortTh
                label='Expenses'
                sortKey='expenses'
                activeKey={sortBy}
                dir={sortDir}
                onSort={onSortColumn}
                align='right'
                title='Non-deleted expenses in those households'
              />
              <TableHead scope='col' className='text-right'>
                Actions
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {usersQuery.isLoading && (
              <TableRow>
                <TableCell colSpan={12} className='py-8 text-center text-nova-muted-foreground'>
                  Loading…
                </TableCell>
              </TableRow>
            )}
            {usersQuery.isError && (
              <TableRow>
                <TableCell colSpan={12} className='py-8 text-center text-red-500'>
                  Failed to load users.
                </TableCell>
              </TableRow>
            )}
            {data?.users.map((u) => (
              <TableRow
                key={u.id}
                className='cursor-pointer'
                onClick={() => {
                  track('Admin User Row Clicked', { source: 'table' });
                  navigate({
                    to: '/admin/users/$userId',
                    params: { userId: String(u.id) },
                    search: { hid: undefined },
                  });
                }}
              >
                <TableCell className='text-nova-muted-foreground'>{u.id}</TableCell>
                <TableCell>{userDisplayName(u)}</TableCell>
                <TableCell>{u.email ?? '—'}</TableCell>
                <TableCell>{u.phone_number ?? '—'}</TableCell>
                <TableCell>
                  {u.is_professional ? (
                    <span className='inline-flex rounded-md bg-violet-100 px-2 py-0.5 text-xs font-semibold text-violet-800'>
                      Yes
                    </span>
                  ) : (
                    <span className='text-xs text-nova-muted-foreground'>—</span>
                  )}
                </TableCell>
                <TableCell>
                  {u.calm_filter_enabled ? (
                    <span className='inline-flex rounded-md bg-sky-100 px-2 py-0.5 text-xs font-semibold text-sky-800'>
                      On
                    </span>
                  ) : (
                    <span className='text-xs text-nova-muted-foreground'>—</span>
                  )}
                </TableCell>
                <TableCell className='text-nova-muted-foreground'>
                  {new Date(u.created_at).toLocaleDateString(undefined, {
                    timeZone: ADMIN_PACIFIC_TZ,
                  })}
                </TableCell>
                <TableCell>
                  {u.subscribed ? (
                    <span className='inline-flex rounded-md bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-700'>
                      Yes{u.subscription_override ? ' (override)' : ''}
                    </span>
                  ) : (
                    <span className='text-xs text-nova-muted-foreground'>No</span>
                  )}
                </TableCell>
                <TableCell>
                  {u.deleted_at ? (
                    <span className='inline-flex rounded-md bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700'>
                      Deleted
                    </span>
                  ) : (
                    <span className='text-xs text-nova-muted-foreground'>—</span>
                  )}
                </TableCell>
                <TableCell className='text-right tabular-nums'>
                  {u.household_message_count.toLocaleString()}
                </TableCell>
                <TableCell className='text-right tabular-nums'>
                  {u.household_expense_count.toLocaleString()}
                </TableCell>
                <TableCell className='text-right'>
                  <Link
                    to='/admin/users/$userId'
                    params={{ userId: String(u.id) }}
                    search={{ hid: undefined }}
                    className={USER_VIEW_LINK_CLASS}
                    onClick={(e) => {
                      e.stopPropagation();
                      track('Admin User Row Clicked', { source: 'table' });
                    }}
                  >
                    View
                  </Link>
                </TableCell>
              </TableRow>
            ))}
            {data && data.users.length === 0 && !usersQuery.isLoading && (
              <TableRow>
                <TableCell colSpan={12} className='py-8 text-center text-nova-muted-foreground'>
                  No users match.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {data && data.total > 0 && (
        <div className='flex items-center justify-between text-sm text-nova-muted-foreground'>
          <span>
            Showing {data.offset + 1}–
            {Math.min(data.offset + data.users.length, data.total)} of {data.total}
          </span>
          <div className='flex gap-2'>
            <Button
              type='button'
              variant='outline'
              size='sm'
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
            >
              Prev
            </Button>
            <span>
              Page {page + 1} / {totalPages}
            </span>
            <Button
              type='button'
              variant='outline'
              size='sm'
              onClick={() => setPage((p) => p + 1)}
              disabled={page >= totalPages - 1}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export const Route = createFileRoute('/admin/')({
  component: AdminUsersPage,
});
